import React, { ReactElement, useContext, useEffect, useMemo, useState } from 'react'

import { SCHEDULEREPORT_NOTSCHEDULED_OPTION_VALUE } from './Constants'

import Advanced from './Advanced'
import ReportScheduling from './ReportScheduling'
import TimeOptions from './TimeOptions'
import Format from './Format'
import ScheduleStatus from './ScheduleStatus'
import { ReportScheduling as ReportSchedulingLanguage } from '../../../../i18n'
import { LanguageContext, ReactState } from '../../Common/Contexts'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import TabActionPromptContext, { TabActionPromptType } from '../../Common/Components/Tabs/TabActionPromptContext'
import AdvancedModeContext, { AdvancedModeContextType } from '../../Common/Contexts/AdvancedModeContext'
import ScheduledEmail from './ScheduledEmails'
import { TelSysModalConfigDTO } from '../../../NonReact/Types/Types'
import { getTelsysMoalConfig } from '../../Common/API/GenericConfigAPI'
import { displayError } from '../../Common/Utils/Messages'
import { JSONResponse } from '../../../NonReact/common/Types/Types'
import { UserInfo, useUserInfo } from '../../../ReactRouting/Hooks/useUserInfo'
import { EmailUtils } from '../../Common/Utils/EmailUtils'
import { EMPTY_STRING } from '../../Common/Constants/Separators'
import { PermissionsUtil } from '../../Common/Utils/PermissionsUtil'

// CSS imports. Commented out because of current build process.
// import './schedulingTab.scss'

/**
 * Component properties type.
 */
type Props = {
    scheduleReportOn: number
    setScheduleReportOn: (newValue: number) => void
    runOnDate: Date | null
    setRunOnDate: (newValue: Date) => void
    runAtTime: Date
    setRunAtTime: (newValue: Date) => void
    runOnDays: string[]
    setRunOnDays: (newValue: string[]) => void
    runAtMinute: Date
    setRunAtMinute: (newValue: Date) => void
    runOnDayInWeek: string
    setRunOnDayInWeek: (newValue: string) => void
    runOnDayInMonth: number
    setRunOnDayInMonth: (newValue: number) => void
    runBetweenStart: Date
    setRunBetweenStart: (newValue: Date) => void
    runBetweenEnd: Date
    setRunBetweenEnd: (newValue: Date) => void
    targetRptFormat: number
    setTargetRptFormat: (newValue: number) => void
    useLanguage: string | undefined
    setUseLanguage: (newValue: string) => void
    paperSize: string | undefined
    setPaperSize: (newValue: string) => void
    autoCalcDuration: boolean
    setAutoCalcDuration: (checked: boolean) => void
    bundleEmailAttachAsZip: boolean
    setBundleEmailAttachAsZip: (checked: boolean) => void
    runWithSimilarSchedules: boolean
    setRunWithSimilarSchedules: (checked: boolean) => void
    deleteSchedule: boolean
    setDeleteSchedule: (newValue: boolean) => void
    resetSchedule: boolean
    setResetSchedule: (newValue: boolean) => void
    nextRunningAt: string
    setNextRunningAt: (newValue: string) => void
    lastRunningAt: string
    setLastRunningAt: (newValue: string) => void
    statusError: string
    setStatusError: (newValue: string) => void
    isScheduleActive: boolean
    selectedEmailsToSend: string
    setSelectedEmailsToSend: (value: string) => void
    setHasValidationWarnings: (value: boolean) => void
}

/**
 * Scheduling tab of report configuration modal window.
 *
 * @param scheduleReportOn Report property.
 * @param setScheduleReportOn Setter for corresponding report property.
 * @param runOnDate Report property.
 * @param setRunOnDate Setter for corresponding report property.
 * @param runAtTime Report property.
 * @param setRunAtTime Setter for corresponding report property.
 * @param runOnDays Report property.
 * @param setRunOnDays Setter for corresponding report property.
 * @param runAtMinute Report property.
 * @param setRunAtMinute Setter for corresponding report property.
 * @param runOnDayInWeek Report property.
 * @param setRunOnDayInWeek Setter for corresponding report property.
 * @param runOnDayInMonth Report property.
 * @param setRunOnDayInMonth Setter for corresponding report property.
 * @param runBetweenStart Report property.
 * @param setRunBetweenStart Setter for corresponding report property.
 * @param runBetweenEnd Report property.
 * @param setRunBetweenEnd Setter for corresponding report property.
 * @param targetRptFormat Report property.
 * @param setTargetRptFormat Setter for corresponding report property.
 * @param useLanguage Report property.
 * @param setUseLanguage Setter for corresponding report property.
 * @param paperSize Report property.
 * @param setPaperSize Setter for corresponding report property.
 * @param autoCalcDuration Report property.
 * @param setAutoCalcDuration Setter for corresponding report property.
 * @param bundleEmailAttachAsZip Report property.
 * @param setBundleEmailAttachAsZip Setter for corresponding report property.
 * @param runWithSimilarSchedules Report property.
 * @param setRunWithSimilarSchedules Setter for corresponding report property.
 * @param deleteSchedule Report property.
 * @param setDeleteSchedule Setter for corresponding report property.
 * @param resetSchedule Report property.
 * @param setResetSchedule Setter for corresponding report property.
 * @param nextRunningAt: String representation of next timestamp the report will run at.
 * @param setNextRunningAt: Setter for corresponding property.
 * @param lastRunningAt: String representation of last timestamp the report has run at.
 * @param setLastRunningAt: Setter for corresponding property.
 * @param statusError: String representation of status or error message for last run of the report.
 * @param setStatusError: Setter for corresponding property.
 * @param isScheduleActive: Report property.
 */
const SchedulingTab: React.FC<Props> = ({
    scheduleReportOn,
    setScheduleReportOn,
    runOnDate,
    setRunOnDate,
    runAtTime,
    setRunAtTime,
    runOnDays,
    setRunOnDays,
    runAtMinute,
    setRunAtMinute,
    runOnDayInWeek,
    setRunOnDayInWeek,
    runOnDayInMonth,
    setRunOnDayInMonth,
    runBetweenStart,
    setRunBetweenStart,
    runBetweenEnd,
    setRunBetweenEnd,
    targetRptFormat,
    setTargetRptFormat,
    useLanguage,
    setUseLanguage,
    paperSize,
    setPaperSize,
    autoCalcDuration,
    setAutoCalcDuration,
    bundleEmailAttachAsZip,
    setBundleEmailAttachAsZip,
    runWithSimilarSchedules,
    setRunWithSimilarSchedules,
    deleteSchedule,
    setDeleteSchedule,
    resetSchedule,
    setResetSchedule,
    nextRunningAt,
    setNextRunningAt,
    lastRunningAt,
    setLastRunningAt,
    statusError,
    setStatusError,
    isScheduleActive,
    selectedEmailsToSend,
    setSelectedEmailsToSend,
    setHasValidationWarnings
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(ReportSchedulingLanguage.NAME)
    const { displayPrompt }: TabActionPromptType = useContext(TabActionPromptContext)
    const { isAdvancedMode }: AdvancedModeContextType = useContext(AdvancedModeContext)

    const [userInfo]: UserInfo = useUserInfo()

    const displaySettings: boolean = SCHEDULEREPORT_NOTSCHEDULED_OPTION_VALUE !== scheduleReportOn
    const displayScheduleStatus: string | false = displaySettings && (nextRunningAt || lastRunningAt || statusError)

    const [telsysModalConfig, setTelsysModalConfig]: ReactState<Nullable<TelSysModalConfigDTO>> =
        useState<Nullable<TelSysModalConfigDTO>>(null)

    const handleResetSchedule = (): void => {
        displayPrompt(
            language.get(ReportSchedulingLanguage.SchedulereportPromptReset),
            language.get(ReportSchedulingLanguage.SchedulereportPromptButtonConfirmLabel),
            language.get(ReportSchedulingLanguage.SchedulereportPromptButtonCancelLabel),
            (): void => {
                setResetSchedule(true)
                setLastRunningAt('')
                setNextRunningAt('')
                setStatusError('')
            },
            undefined
        )
    }

    const shouldDisplayScheduledEmails: boolean = useMemo(
        () =>
            EmailUtils.shouldDisplayByBlacklist(
                telsysModalConfig?.ReportEmailDomainBlacklist,
                userInfo?.EmailAddr ?? EMPTY_STRING
            ),
        [telsysModalConfig?.ReportEmailDomainBlacklist, userInfo?.EmailAddr]
    )

    const isCMEUser: boolean = PermissionsUtil.isCMEWideUser(userInfo)

    useEffect((): void => {
        getTelsysMoalConfig()
            .then((config: JSONResponse<TelSysModalConfigDTO>) => setTelsysModalConfig(config))
            .catch(displayError)
    }, [])

    return (
        <div className='scheduling-tab tab-area'>
            <ReportScheduling
                scheduleReportOn={scheduleReportOn}
                setScheduleReportOn={setScheduleReportOn}
                isScheduleActive={isScheduleActive}
                setDeleteSchedule={setDeleteSchedule}
                handleResetSchedule={handleResetSchedule}
                resetSchedule={resetSchedule}
                deleteSchedule={deleteSchedule}
            />
            {displaySettings && (
                <React.Fragment>
                    {shouldDisplayScheduledEmails && !isCMEUser ? (
                        <ScheduledEmail
                            selectedEmailsToSend={selectedEmailsToSend}
                            setSelectedEmailsToSend={setSelectedEmailsToSend}
                            telSysModalConfig={telsysModalConfig}
                            setHasValidationWarnings={setHasValidationWarnings}
                        />
                    ) : (
                        <></>
                    )}
                    <TimeOptions
                        scheduleReportOn={scheduleReportOn}
                        runOnDate={runOnDate}
                        setRunOnDate={setRunOnDate}
                        runAtTime={runAtTime}
                        setRunAtTime={setRunAtTime}
                        runOnDays={runOnDays}
                        setRunOnDays={setRunOnDays}
                        runAtMinute={runAtMinute}
                        setRunAtMinute={setRunAtMinute}
                        runOnDayInWeek={runOnDayInWeek}
                        setRunOnDayInWeek={setRunOnDayInWeek}
                        runOnDayInMonth={runOnDayInMonth}
                        setRunOnDayInMonth={setRunOnDayInMonth}
                        runBetweenStart={runBetweenStart}
                        setRunBetweenStart={setRunBetweenStart}
                        runBetweenEnd={runBetweenEnd}
                        setRunBetweenEnd={setRunBetweenEnd}
                    />
                    <Format
                        targetRptFormat={targetRptFormat}
                        setTargetRptFormat={setTargetRptFormat}
                        useLanguage={useLanguage}
                        setUseLanguage={setUseLanguage}
                        paperSize={paperSize}
                        setPaperSize={setPaperSize}
                    />
                    {isAdvancedMode ? (
                        <Advanced
                            autoCalcDuration={autoCalcDuration}
                            setAutoCalcDuration={setAutoCalcDuration}
                            bundleEmailAttachAsZip={bundleEmailAttachAsZip}
                            setBundleEmailAttachAsZip={setBundleEmailAttachAsZip}
                            runWithSimilarSchedules={runWithSimilarSchedules}
                            setRunWithSimilarSchedules={setRunWithSimilarSchedules}
                        />
                    ) : (
                        <></>
                    )}
                    {displayScheduleStatus && (
                        <ScheduleStatus
                            nextRunningAt={nextRunningAt}
                            lastRunAt={lastRunningAt}
                            statusError={statusError}
                        />
                    )}
                </React.Fragment>
            )}
        </div>
    )
}

export default SchedulingTab
